// Libs
import classNames from 'classnames/bind';
import { Button, DateInput, Flex, Heading, Table, TextInput, View, Tooltip, Text, Spinner } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import { ITuitionForm } from '~/utils/mockData/common';
import { DATE_FORMAT, DEFAULT_LIMIT } from '~/utils/constants/common';
import { getStudents } from '~/thunks/user/userThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { defaultValuesForm, schema } from './helper';
import { createTuition, getListTuitions } from '~/thunks/tuition/tuitionThunk';
import { IBodyCreateTuition } from '~/utils/interface/tuition';
import { TuitionStatusEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './TuitionArea.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const TuitionArea = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/tuition']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ITuitionForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { students } = useAppSelector((state) => state.userState);
  const { isRefreshListTuition, tuitions, isLoadingCreateTuition } = useAppSelector((state) => state.tuitionState);
  //#endregion Selector

  //#region Declare State
  const studentOptions = students.map((student) => ({
    value: student.id,
    label: student.fullName,
  }));

  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetStudents();
    handleGetTuitions();
  }, []);

  useEffect(() => {
    if (isRefreshListTuition) {
      handleGetTuitions();
    }
  }, [isRefreshListTuition]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleBack = () => {
    navigate(-1);
  };

  const handleGetStudents = async () => {
    await dispatch(getStudents()).unwrap();
  };

  const handleGetTuitions = async () => {
    await dispatch(
      getListTuitions({
        limit: DEFAULT_LIMIT,
      })
    ).unwrap();
  };

  const handleCreateTuition = async (payload: IBodyCreateTuition) => {
    await dispatch(createTuition(payload))
      .unwrap()
      .then((res) => {
        reset();
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleSubmitForm = (data: ITuitionForm) => {
    if (!data) return;

    const payload: IBodyCreateTuition = {
      name: data.name,
      studentId: data.studentId,
      studentName: data.studentName,
      paidDate: data.paidDate,
    };

    handleCreateTuition(payload);
  };
  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('tuitionTitle')}>
          <Heading level='h3'>{t('tuition_area_title', { ns: 'admin/tuition' })}</Heading>
          <img className={cx('tuitionIcon')} src={icons.commonIconTuitionArea} alt={t('common_alt_img')} />
        </View>

        <View as='div' className={cx('tuitionForm')} margin='medium 0' width='100%'>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <View as='div' className={cx('tuitionInputContainer')}>
              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='studentId'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_select_student_label', { ns: 'admin/tuition' })}
                      </label>
                      <BaseSelect
                        value={value}
                        options={studentOptions}
                        onChange={(e) => {
                          onChange(e.value);
                          setValue('studentName', e.label);
                        }}
                      />
                      {errors?.studentId?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.studentId?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>

              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='name'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_insert_tuition_name_label', { ns: 'admin/tuition' })}
                      </label>
                      <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
                      {errors?.name?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.name?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>

              <View as='div' className={cx('tuitionInput')}>
                <Controller
                  name='paidDate'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <label className={cx('tuitionLabel')}>
                        {t('tuition_area_insert_tuition_status_date_label', { ns: 'admin/tuition' })}
                      </label>
                      <View width='100%'>
                        <DateInput
                          display='block'
                          renderLabel=''
                          value={value}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>, value: { value: string }) => {
                            if (value.value) {
                              const formattedDate = moment(value.value, DATE_FORMAT.LONG_FORMAT).format(
                                DATE_FORMAT.DATE
                              );
                              onChange(formattedDate);
                            }
                          }}
                        />
                      </View>
                      {errors?.paidDate?.message && (
                        <View className={cx('errorMessage')}>
                          <Text variant='legend' color='danger'>
                            {errors?.paidDate?.message}
                          </Text>
                        </View>
                      )}
                    </>
                  )}
                />
              </View>
            </View>

            <Flex justifyItems='center' margin='small 0 0 0'>
              <View as='div' className={cx('tuitionButton')}>
                <Button
                  type='submit'
                  color='primary'
                  disabled={isLoadingCreateTuition}
                  renderIcon={isLoadingCreateTuition && <Spinner size='x-small' />}
                >
                  {t('tuition_area_add_tuition_label', { ns: 'admin/tuition' })}
                </Button>
              </View>
            </Flex>
          </form>
        </View>

        <View as='div' className={cx('tuitionTableDesktop')}>
          <Table layout='auto'>
            <Table.Head></Table.Head>
            <Table.Body>
              {tuitions.map((item, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <div className={cx('tuitionTable')}>
                      <Tooltip renderTip={item.studentName}>
                        <span>{item.studentName}</span>
                      </Tooltip>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('tableCell')}>
                      <div className={cx('tuitionTable')}>
                        <img
                          src={icons.commonIconTuitionFees}
                          alt={t('common_alt_img')}
                          className={cx('tuitionFeeIcon')}
                        />
                        <Tooltip renderTip={item.name}>
                          <span>{item.name}</span>
                        </Tooltip>
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('tableCell')}>
                      <div className={cx('tuitionTable')}>
                        <img
                          className={cx('iconCalendar')}
                          src={icons.commonIconCalendarDay}
                          alt={t('common_alt_img')}
                        />
                        <Tooltip
                          renderTip={
                            item.status === TuitionStatusEnum.PAID
                              ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                              : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })
                          }
                        >
                          <span>
                            {item.status === TuitionStatusEnum.PAID
                              ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                              : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </View>

        <View className={cx('tuitionTableMobile')}>
          {tuitions.map((item, index) => (
            <View
              key={index}
              as='div'
              className={cx('tuitionCard')}
              padding='small'
              borderWidth='small'
              borderRadius='medium'
            >
              <Heading level='h4' margin='0 0 small 0'>
                <Tooltip renderTip={item.studentName}>
                  <span>{item.studentName}</span>
                </Tooltip>
              </Heading>

              <Flex gap='small' alignItems='center' margin='small 0'>
                <div className={cx('tuitionTable')}>
                  <img src={icons.commonIconTuitionFees} alt={t('common_alt_img')} className={cx('tuitionFeeIcon')} />
                  <Tooltip renderTip={item.name}>
                    <span>{item.name}</span>
                  </Tooltip>
                </div>
              </Flex>

              <Flex gap='small' alignItems='center' margin='small 0'>
                <div className={cx('tuitionTable')}>
                  <img className={cx('iconCalendar')} src={icons.commonIconCalendarDay} alt={t('common_alt_img')} />
                  <Tooltip
                    renderTip={
                      item.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                        : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })
                    }
                  >
                    <span>
                      {item.status === TuitionStatusEnum.PAID
                        ? `${t('tuition_area_paid_on_title', { ns: 'admin/tuition' })} ${item.paidDate}`
                        : t('tuition_area_unpaid_title', { ns: 'admin/tuition' })}
                    </span>
                  </Tooltip>
                </div>
              </Flex>
            </View>
          ))}
        </View>
      </div>
    </div>
  );
};

export default TuitionArea;
